import {
  AccountCircle,
  Article,
  Assignment,
  CalendarToday,
  ChevronLeft,
  Dashboard,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  LocalHospital,
  Menu as MenuIcon,
  Message,
  MonitorWeight,
  People,
  PersonAdd,
  Restaurant,
  Settings,
  SmartToy,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { auth } from '../firebase';
import { clientApi } from '../services/clientApi';
import { dietitianApi } from '../services/dietitianApi';
import Logo from './Logo';
import NotificationBell from './NotificationBell';
import ProfileModal from './ProfileModal';

interface SidebarProps {
  userRole: 'dietitian' | 'client';
  activeTab: string;
  onTabChange: (tab: string) => void;
  onWidthChange?: (width: number) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  userRole,
  activeTab,
  onTabChange,
  onWidthChange,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { unreadMessageCount } = useNotification();
  const { clearAuth, client: authClient } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userImage, setUserImage] = useState('');
  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const [messagesMenuOpen, setMessagesMenuOpen] = useState(false);
  const [isSelfRegistered, setIsSelfRegistered] = useState(false);
  const [hasDietitian, setHasDietitian] = useState(false);

  const isCollapsed = !isMobile && desktopCollapsed;
  const sidebarWidth = isCollapsed ? 64 : 280;

  // Parent component'e genişlik değişikliğini bildir
  useEffect(() => {
    if (onWidthChange) {
      onWidthChange(sidebarWidth);
    }
  }, [sidebarWidth, onWidthChange]);

  const loadUserData = useCallback(async () => {
    try {
      const email = localStorage.getItem('userEmail') || '';
      setUserEmail(email);

      if (userRole === 'dietitian') {
        try {
          const dietitianData = await dietitianApi.getDietitianByEmail(email);
          setUserName(
            `${dietitianData.firstName} ${dietitianData.lastName}`.trim()
          );
          setUserImage(dietitianData.profileImageUrl || '');
        } catch (error) {
          // Diyetisyen veritabanında yoksa, email'i göster
          console.log('Dietitian not found in database');
          setUserName('');
          setUserImage('');
        }
      } else if (userRole === 'client') {
        try {
          const clientData = await clientApi.getClientByEmail(email);
          setUserName(`${clientData.firstName} ${clientData.lastName}`.trim());
          setUserImage(clientData.profileImageUrl || '');
          setIsSelfRegistered(clientData.isSelfRegistered ?? true);
          setHasDietitian(!!(clientData.dietitianId || clientData.dietitianEmail));
        } catch (error) {
          console.log('Client not found in database, retrying...', error);
          setTimeout(async () => {
            try {
              const retryClientData = await clientApi.getClientByEmail(email);
              setUserName(
                `${retryClientData.firstName} ${retryClientData.lastName}`.trim()
              );
              setUserImage(retryClientData.profileImageUrl || '');
              setIsSelfRegistered(retryClientData.isSelfRegistered ?? true);
              setHasDietitian(!!(retryClientData.dietitianId || retryClientData.dietitianEmail));
            } catch (retryError) {
              console.log('Client still not found after retry');
              setUserName('');
              setUserImage('');
              setIsSelfRegistered(true);
              setHasDietitian(false);
            }
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, [userRole]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopCollapsed(!desktopCollapsed);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();

      // Tüm kullanıcı verilerini temizle
      const currentRole = localStorage.getItem('userRole');

      // Tüm kullanıcıya özel localStorage verilerini temizle
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('dietitianData');
      localStorage.removeItem('clientData');
      localStorage.removeItem('client-onboarding-completed');

      // TaroBot mesajlarını temizle
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('taro-messages-')) {
          localStorage.removeItem(key);
        }
        // Bildirimleri temizle
        if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
          localStorage.removeItem(key);
        }
      });

      // Context'i temizle
      clearAuth();

      // Role göre yönlendir
      if (currentRole === 'dietitian') {
        navigate('/dietitian/login');
      } else {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleTabClick = (tab: string) => {
    // Navigate to the appropriate route
    if (userRole === 'dietitian') {
      navigate(`/dietitian/${tab === 'dashboard' ? '' : tab}`);
    } else if (userRole === 'client') {
      navigate(`/client/${tab === 'dashboard' ? '' : tab}`);
    }

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  interface MenuItem {
    id: string;
    label: string;
    icon: React.ReactElement;
    path: string;
    hasSubmenu?: boolean;
    submenu?: MenuItem[];
  }

  const dietitianMenuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      path: '/dietitian',
    },
    {
      id: 'clients',
      label: 'Danışanlar',
      icon: <People />,
      path: '/dietitian/clients',
    },
    {
      id: 'appointments',
      label: 'Randevular',
      icon: <CalendarToday />,
      path: '/dietitian/appointments',
    },
    {
      id: 'programs',
      label: 'Diyet Programları',
      icon: <Assignment />,
      path: '/dietitian/programs',
    },
    {
      id: 'messages',
      label: 'Mesajlar',
      icon: <Message />,
      path: '/dietitian/messages',
      hasSubmenu: true,
      submenu: [
        {
          id: 'messages',
          label: 'Danışan Mesajları',
          icon: <Message />,
          path: '/dietitian/messages',
        },
        {
          id: 'automated-messages',
          label: 'Otomatik Mesajlar',
          icon: <SmartToy />,
          path: '/dietitian/automated-messages',
        },
        {
          id: 'client-requests',
          label: 'İstek Yönetimi',
          icon: <PersonAdd />,
          path: '/dietitian/client-requests',
        },
      ],
    },
    {
      id: 'content',
      label: 'İçerik Yönetimi',
      icon: <Article />,
      path: '/dietitian/content',
    },
  ];

  const baseClientMenuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      path: '/client',
    },
    {
      id: 'messages',
      label: 'Mesajlar',
      icon: <Message />,
      path: '/client/messages',
    },
    {
      id: 'nutrition',
      label: 'Yemek Takibi',
      icon: <Restaurant />,
      path: '/client/nutrition',
    },
    {
      id: 'weight',
      label: 'Kilo Takibi',
      icon: <MonitorWeight />,
      path: '/client/weight',
    },
    {
      id: 'my-dietitian',
      label: 'Paylaşımlar',
      icon: <LocalHospital />,
      path: '/client/my-dietitian',
    },
  ];

  // Danışan için güncel veri: context'teki client varsa onu kullan (diyetisyen kabul sonrası ışık sönsün)
  const effectiveIsSelfRegistered =
    userRole === 'client' && authClient
      ? (authClient.isSelfRegistered ?? true)
      : isSelfRegistered;
  const effectiveHasDietitian =
    userRole === 'client' && authClient
      ? !!(authClient.dietitianId || authClient.dietitianEmail)
      : hasDietitian;

  // Kendi üye olan danışanlar için "Diyetisyenim" sekmesi ekle
  const clientMenuItems =
    userRole === 'client' && effectiveIsSelfRegistered
      ? [
        ...baseClientMenuItems,
        {
          id: 'my-dietitian-settings',
          label: 'Diyetisyenim',
          icon: <AccountCircle />,
          path: '/client/my-dietitian-settings',
        },
      ]
      : baseClientMenuItems;

  const menuItems: MenuItem[] =
    userRole === 'dietitian' ? dietitianMenuItems : clientMenuItems;

  const drawer = (
    <Box
      onTouchStart={(e) => {
        // Sidebar içindeyken sayfa scroll'unu engelle
        const target = e.target as HTMLElement;
        if (target.closest('.MuiDrawer-paper')) {
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        // Sidebar içindeyken sayfa scroll'unu engelle
        const target = e.target as HTMLElement;
        if (target.closest('.MuiDrawer-paper')) {
          e.stopPropagation();
        }
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #2E7D32 0%, #1B5E20 100%)',
        color: 'white',
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        // Scrollbar'ı gizle ama scroll yapılabilir olsun
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Opera
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            repeating-linear-gradient(
              45deg,
              transparent,
              transparent 60px,
              rgba(255, 255, 255, 0.025) 80px,
              rgba(255, 255, 255, 0.025) 160px
            )
          `,
          pointerEvents: 'none',
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 15%, rgba(255, 255, 255, 0.05) 0%, transparent 40%),
            radial-gradient(circle at 80% 85%, rgba(255, 255, 255, 0.04) 0%, transparent 40%)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        },
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: isCollapsed ? 2 : isMobile ? 2.5 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          gap: isCollapsed ? 0 : 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          minHeight: isCollapsed ? 64 : isMobile ? 72 : 'auto',
          ...(isMobile && {
            pt: 3.5, // Safe area için ekstra padding
            pb: 2.5,
          }),
        }}
      >
        {!isCollapsed && (
          <>
            <Logo size={48} animate />
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}
                >
                  Dietaro
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {userRole === 'dietitian' ? 'Diyetisyen' : 'Danışan'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NotificationBell size="small" />
              </Box>
            </Box>
          </>
        )}

        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: isCollapsed ? 'static' : 'static',
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            width: isCollapsed ? 40 : 36,
            height: isCollapsed ? 40 : 36,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
            ...(isMobile && {
              width: 44,
              height: 44,
            }),
          }}
        >
          {isCollapsed ? <MenuIcon /> : <ChevronLeft />}
        </IconButton>
      </Box>

      {/* User Profile Section */}
      {!isCollapsed && (
        <Box
          sx={{
            p: isMobile ? 2 : 2.5,
            mx: isMobile ? 1.5 : 2,
            mt: isMobile ? 1.5 : 2,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            transition: 'all 0.2s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={userImage}
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'white',
                color: '#2E7D32',
                fontWeight: 700,
                fontSize: '1.2rem',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              }}
            >
              {userName
                ? userName.charAt(0).toUpperCase()
                : userEmail.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userName || 'Kullanıcı'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.8,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userEmail}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      {isCollapsed && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            mb: 1,
          }}
        >
          <Avatar
            src={userImage}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'white',
              color: '#2E7D32',
              fontWeight: 700,
              fontSize: '1rem',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            {userName
              ? userName.charAt(0).toUpperCase()
              : userEmail.charAt(0).toUpperCase()}
          </Avatar>
        </Box>
      )}

      {/* Navigation Menu */}
      <List
        sx={{
          flex: 1,
          px: isCollapsed ? 0.5 : isMobile ? 1 : 1.5,
          py: isMobile ? 1.5 : 2,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          // Scrollbar'ı gizle ama scroll yapılabilir olsun
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Opera
          },
        }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {menuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem disablePadding sx={{ mb: isMobile ? 0.75 : 0.5 }}>
              <ListItemButton
                onClick={() => {
                  if (item.hasSubmenu && !isCollapsed) {
                    // Alt menü varsa sadece aç/kapat, yönlendirme yapma
                    setMessagesMenuOpen(!messagesMenuOpen);
                  } else {
                    handleTabClick(item.id);
                  }
                }}
                selected={activeTab === item.id || (item.hasSubmenu && (activeTab === 'messages' || activeTab === 'automated-messages' || activeTab === 'client-requests'))}
                data-tour={item.id}
                title={isCollapsed ? item.label : undefined}
                sx={{
                  borderRadius: 2,
                  py: isCollapsed ? 1.25 : isMobile ? 1.75 : 1.5,
                  px: isCollapsed ? 0.5 : isMobile ? 1.5 : 1.5,
                  minHeight: isMobile ? 56 : 'auto',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:active': {
                    transform: isMobile ? 'scale(0.98)' : isCollapsed ? 'scale(1.1)' : 'translateX(4px)',
                  },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    transform: isMobile ? 'none' : isCollapsed ? 'scale(1.1)' : 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    boxShadow: isMobile ? 'inset 4px 0 0 rgba(255, 255, 255, 0.3)' : 'none',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.25)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'white',
                    minWidth: isCollapsed ? 0 : isMobile ? 44 : 40,
                    justifyContent: 'center',
                    '& svg': {
                      fontSize: isMobile ? 26 : 24,
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: isMobile ? '1rem' : '0.95rem',
                        fontWeight: activeTab === item.id ? 600 : 500,
                        letterSpacing: isMobile ? '0.01em' : 'normal',
                      }}
                    />
                    {item.hasSubmenu && (
                      <Box sx={{ ml: 'auto' }}>
                        {messagesMenuOpen ? (
                          <ExpandLess sx={{ color: 'white', fontSize: 20 }} />
                        ) : (
                          <ExpandMore sx={{ color: 'white', fontSize: 20 }} />
                        )}
                      </Box>
                    )}
                  </>
                )}
                {/* Okunmamış mesaj varsa yanıp sönen badge */}
                {item.id === 'messages' && unreadMessageCount > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: isCollapsed ? 8 : item.hasSubmenu ? 40 : 12,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#FF5722',
                      boxShadow: '0 0 8px rgba(255, 87, 34, 0.8)',
                      transform: 'translateY(-50%)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          opacity: 1,
                          transform: 'translateY(-50%) scale(1)',
                          boxShadow: '0 0 8px rgba(255, 87, 34, 0.8)',
                        },
                        '50%': {
                          opacity: 0.4,
                          transform: 'translateY(-50%) scale(1.3)',
                          boxShadow: '0 0 16px rgba(255, 87, 34, 1)',
                        },
                      },
                    }}
                  />
                )}
                {/* Kendi üye olan ve diyetisyeni olmayan danışanlar için "Diyetisyenim" yanında yanıp sönen ışık */}
                {item.id === 'my-dietitian-settings' && effectiveIsSelfRegistered && !effectiveHasDietitian && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: isCollapsed ? 8 : item.hasSubmenu ? 40 : 12,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#FFD700',
                      boxShadow: '0 0 8px rgba(255, 215, 0, 0.8)',
                      transform: 'translateY(-50%)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          opacity: 1,
                          transform: 'translateY(-50%) scale(1)',
                          boxShadow: '0 0 8px rgba(255, 215, 0, 0.8)',
                        },
                        '50%': {
                          opacity: 0.4,
                          transform: 'translateY(-50%) scale(1.3)',
                          boxShadow: '0 0 16px rgba(255, 215, 0, 1)',
                        },
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
            {/* Submenu */}
            {item.hasSubmenu && !isCollapsed && (
              <Collapse in={messagesMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.submenu?.map((subItem: MenuItem) => (
                    <ListItem key={subItem.id} disablePadding sx={{ mb: 0.5, pl: 4 }}>
                      <ListItemButton
                        onClick={() => handleTabClick(subItem.id)}
                        selected={activeTab === subItem.id}
                        sx={{
                          borderRadius: 2,
                          py: 1.25,
                          px: 1.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            transform: 'translateX(4px)',
                          },
                          '&.Mui-selected': {
                            background: 'rgba(255, 255, 255, 0.15)',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.2)',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: 'white',
                            minWidth: 32,
                            '& svg': {
                              fontSize: 20,
                            },
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: activeTab === subItem.id ? 600 : 500,
                          }}
                        />
                      </ListItemButton>
                      {/* Submenu için de bildirim badge'i */}
                      {item.id === 'messages' && subItem.id === 'messages' && unreadMessageCount > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 12, // Submenu olduğu için biraz daha sağa yanaşık olabilir ya da standart
                            width: 6, // Biraz daha küçük olabilir
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#FF5722',
                            boxShadow: '0 0 8px rgba(255, 87, 34, 0.8)',
                            transform: 'translateY(-50%)',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%, 100%': {
                                opacity: 1,
                                transform: 'translateY(-50%) scale(1)',
                                boxShadow: '0 0 8px rgba(255, 87, 34, 0.8)',
                              },
                              '50%': {
                                opacity: 0.4,
                                transform: 'translateY(-50%) scale(1.3)',
                                boxShadow: '0 0 16px rgba(255, 87, 34, 1)',
                              },
                            },
                            pointerEvents: 'none', // Tıklamayı engellemesin
                          }}
                        />
                      )}
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      {!isCollapsed && (
        <>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Menu - Collapsible */}
          <List sx={{ px: 1.5, py: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setBottomMenuOpen(!bottomMenuOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  mt: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ListItemText
                  primary="Daha Fazla"
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    sx: { opacity: 0.9 },
                  }}
                />
                {bottomMenuOpen ? (
                  <ExpandLess sx={{ color: 'white' }} />
                ) : (
                  <ExpandMore sx={{ color: 'white' }} />
                )}
              </ListItemButton>
            </ListItem>

            <Collapse in={bottomMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      setProfileModalOpen(true);
                      if (isMobile) {
                        setBottomMenuOpen(false);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      ml: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <AccountCircle />
                    </ListItemIcon>
                    <ListItemText
                      primary="Profil"
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      if (userRole === 'dietitian') {
                        navigate('/dietitian/settings');
                      } else if (userRole === 'client') {
                        navigate('/client/settings');
                      }
                      if (isMobile) {
                        setMobileOpen(false);
                        setBottomMenuOpen(false);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      ml: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <Settings />
                    </ListItemIcon>
                    <ListItemText
                      primary="Ayarlar"
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      ml: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(244, 67, 54, 0.2)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      <ExitToApp />
                    </ListItemIcon>
                    <ListItemText
                      primary="Çıkış Yap"
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </List>
        </>
      )}
      {isCollapsed && (
        <List sx={{ px: 0.5, py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setProfileModalOpen(true)}
              title="Profil"
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 0.5,
                justifyContent: 'center',
                mt: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'white',
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <AccountCircle />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                if (userRole === 'dietitian') {
                  navigate('/dietitian/settings');
                } else if (userRole === 'client') {
                  navigate('/client/settings');
                }
              }}
              title="Ayarlar"
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 0.5,
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'white',
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <Settings />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              title="Çıkış Yap"
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 0.5,
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(244, 67, 54, 0.2)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'white',
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <ExitToApp />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {/* Profile Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          loadUserData(); // Reload user data after profile update
        }}
        onProfileUpdate={loadUserData}
      />
    </Box>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && !mobileOpen && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 22,
            left: 16,
            width: 48,
            height: 48,
            zIndex: 1400,
            bgcolor: '#2E7D32',
            color: 'white',
            boxShadow: '0 8px 24px rgba(46, 125, 50, 0.4), 0 4px 8px rgba(0, 0, 0, 0.2)',
            border: 'none',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            '&:active': {
              bgcolor: '#1B5E20',
              transform: 'scale(0.95)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <MenuIcon sx={{ fontSize: 28 }} />
        </IconButton>
      )}

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
          onTouchStart: (e) => {
            // Sidebar içindeyken sayfa scroll'unu engelle
            e.stopPropagation();
          },
          onTouchMove: (e) => {
            // Sidebar içindeyken sayfa scroll'unu engelle
            e.stopPropagation();
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
            backdropFilter: isMobile ? 'blur(4px)' : 'none',
            transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 0.3s ease',
          },
          '& .MuiDrawer-paper': {
            width: sidebarWidth,
            boxSizing: 'border-box',
            border: 'none',
            borderRight: isCollapsed ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
            boxShadow: isMobile
              ? '0 8px 32px rgba(0, 0, 0, 0.3), -4px 0 16px rgba(0, 0, 0, 0.1)'
              : isCollapsed
                ? 'none'
                : '4px 0 12px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            transition: isMobile
              ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'width 0.3s ease, border 0.3s ease, box-shadow 0.3s ease',
            ...(isMobile && {
              maxWidth: '85vw',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }),
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;
